import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader, AdminBody } from "./components/AdminHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import MediaField from "./components/MediaField";

const PAGE_TEXTS = [
  { key: "home", label: "Accueil", fields: [
    { name: "speakers_kicker", label: "Section intervenants — surtitre" }, { name: "speakers_title", label: "Section intervenants — titre" },
    { name: "articles_kicker", label: "Section articles — surtitre" }, { name: "articles_title", label: "Section articles — titre" },
  ], note: "Laissez le surtitre intervenants vide pour reprendre automatiquement l'année de l'édition mise en avant." },
  { key: "edition", label: "Édition active", fields: [], note: "Le thème, le résumé, les CTA d'inscription et le message d'ouverture/fermeture se gèrent dans Éditions (chaque édition)." },
  { key: "programme", label: "Programme", fields: [
    { name: "kicker", label: "Surtitre (vide = Édition {année})" }, { name: "title", label: "Titre (vide = Programme — {thème})" }, { name: "lead", label: "Introduction (vide = résumé de l'édition)", textarea: true },
    { name: "seo_title", label: "SEO title" }, { name: "seo_description", label: "Meta description", textarea: true },
  ], note: "Laissez vide pour reprendre automatiquement l'année / le thème / le résumé de l'édition mise en avant. Remplissez uniquement pour forcer un texte." },
  { key: "intervenants", label: "Intervenants", fields: [
    { name: "kicker", label: "Surtitre" }, { name: "title", label: "Titre (vide = ...édition {année})" }, { name: "lead", label: "Introduction", textarea: true },
    { name: "seo_title", label: "SEO title" }, { name: "seo_description", label: "Meta description", textarea: true },
  ], note: "Laissez le titre vide pour reprendre automatiquement l'année de l'édition mise en avant." },
  { key: "articles", label: "Articles", fields: [
    { name: "kicker", label: "Surtitre" }, { name: "title", label: "Titre" }, { name: "lead", label: "Introduction", textarea: true },
    { name: "seo_title", label: "SEO title" }, { name: "seo_description", label: "Meta description", textarea: true },
  ]},
  { key: "partenaires", label: "Partenaires", fields: [
    { name: "kicker", label: "Surtitre" }, { name: "title", label: "Titre" }, { name: "lead", label: "Introduction", textarea: true },
    { name: "cta_title", label: "Bloc CTA — titre" }, { name: "cta_text", label: "Bloc CTA — texte", textarea: true }, { name: "cta_button", label: "Bloc CTA — bouton" },
    { name: "seo_title", label: "SEO title" }, { name: "seo_description", label: "Meta description", textarea: true },
  ]},
  { key: "archives", label: "Archives", fields: [
    { name: "kicker", label: "Surtitre" }, { name: "title", label: "Titre" },
    { name: "seo_title", label: "SEO title" }, { name: "seo_description", label: "Meta description", textarea: true },
  ]},
  { key: "contact", label: "Contact", fields: [], note: "Les coordonnées se gèrent dans l'onglet « Contact ». Aucun réglage SEO spécifique n'est utilisé pour cette page." },
];

const Field = ({ label, value, onChange, textarea, rows }) => (
  <div>
    <label className="block text-sm font-medium text-f2b-text mb-1.5">{label}</label>
    {textarea ? <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows || 3} />
      : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />}
  </div>
);

const SettingsAdmin = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings-admin"], queryFn: async () => (await api.get("/settings")).data });
  const [s, setS] = useState(null);
  const [pageKey, setPageKey] = useState("home");
  useEffect(() => { if (data) setS(JSON.parse(JSON.stringify(data))); }, [data]);

  const save = useMutation({
    mutationFn: async (payload) => (await api.put("/admin/settings", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["settings-admin"] }); toast.success("Réglages enregistrés"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Erreur"),
  });

  if (!s) return <><AdminHeader title="Réglages & SEO" /><AdminBody><p className="text-f2b-gray">Chargement…</p></AdminBody></>;

  const upd = (path) => (val) => {
    setS((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let o = next;
      for (let i = 0; i < keys.length - 1; i++) { o[keys[i]] = { ...o[keys[i]] }; o = o[keys[i]]; }
      o[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const metrics = s.key_metrics || [];
  const setMetric = (i, k, v) => setS((prev) => { const m = [...(prev.key_metrics || [])]; m[i] = { ...m[i], [k]: v }; return { ...prev, key_metrics: m }; });

  return (
    <>
      <AdminHeader title="Réglages & SEO" subtitle="Textes de page, chiffres clés, CTA et référencement">
        <Button onClick={() => save.mutate(s)} disabled={save.isPending} className="bg-f2b-cyan text-f2b-black hover:bg-f2b-cyan/90" data-testid="settings-save">
          {save.isPending ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </AdminHeader>
      <AdminBody>
        <Tabs defaultValue="hero" className="max-w-3xl">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="hero" data-testid="tab-hero">Hero</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages">Textes des pages</TabsTrigger>
            <TabsTrigger value="metrics" data-testid="tab-metrics">Chiffres clés</TabsTrigger>
            <TabsTrigger value="next">Prochaine édition</TabsTrigger>
            <TabsTrigger value="about">À propos</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="home" data-testid="tab-home">Mode Home</TabsTrigger>
            <TabsTrigger value="themes" data-testid="tab-themes">Thématiques</TabsTrigger>
            <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
            <TabsTrigger value="legal">Légal</TabsTrigger>
          </TabsList>

          <TabsContent value="hero" className="space-y-4">
            <Field label="Kicker" value={s.hero?.kicker} onChange={upd("hero.kicker")} />
            <Field label="Titre" value={s.hero?.title} onChange={upd("hero.title")} />
            <Field label="Accroche" value={s.hero?.lead} onChange={upd("hero.lead")} textarea />
            <p className="text-sm text-f2b-gray bg-f2b-light p-3 border border-f2b-filet">Le grand titre, l'image et les boutons de la Home proviennent automatiquement de l'édition mise en avant et du Mode Home. Kicker / Titre / Accroche ne servent que de repli si l'édition ne renseigne pas l'information.</p>
          </TabsContent>

          <TabsContent value="pages" className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-f2b-text mb-1.5">Page</label>
              <Select value={pageKey} onValueChange={setPageKey}>
                <SelectTrigger className="w-64" data-testid="pages-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAGE_TEXTS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {PAGE_TEXTS.find((p) => p.key === pageKey)?.note && (
              <p className="text-sm text-f2b-gray bg-f2b-light p-3 border border-f2b-filet">{PAGE_TEXTS.find((p) => p.key === pageKey).note}</p>
            )}
            {PAGE_TEXTS.find((p) => p.key === pageKey)?.fields.map((f) => (
              <Field key={f.name} label={f.label} textarea={f.textarea}
                value={s.pages?.[pageKey]?.[f.name]} onChange={upd(`pages.${pageKey}.${f.name}`)} />
            ))}
          </TabsContent>

          <TabsContent value="metrics" className="space-y-4">
            {metrics.map((m, i) => (
              <div key={i} className="flex gap-3 items-end">
                <div className="flex-1"><Field label="Valeur" value={m.value} onChange={(v) => setMetric(i, "value", v)} /></div>
                <div className="flex-[2]"><Field label="Libellé" value={m.label} onChange={(v) => setMetric(i, "label", v)} /></div>
                <button onClick={() => setS((p) => ({ ...p, key_metrics: p.key_metrics.filter((_, idx) => idx !== i) }))} className="text-f2b-gray hover:text-red-500 pb-2"><Trash2 size={18} /></button>
              </div>
            ))}
            <Button variant="outline" onClick={() => setS((p) => ({ ...p, key_metrics: [...(p.key_metrics || []), { value: "", label: "" }] }))}><Plus size={16} className="mr-1" /> Ajouter un chiffre</Button>
          </TabsContent>

          <TabsContent value="next" className="space-y-4">
            <Field label="Titre" value={s.next_edition?.title} onChange={upd("next_edition.title")} />
            <Field label="Texte" value={s.next_edition?.text} onChange={upd("next_edition.text")} textarea />
            <p className="text-sm text-f2b-gray">Titre et texte vides = repris automatiquement de l'édition à venir. Le bouton (« S'inscrire » / « Être informé ») est généré selon le statut d'inscription.</p>
          </TabsContent>

          <TabsContent value="about" className="space-y-4">
            <Field label="Titre" value={s.about?.title} onChange={upd("about.title")} />
            <Field label="Texte" value={s.about?.text} onChange={upd("about.text")} textarea rows={4} />
            <Field label="Positionnement" value={s.about?.positioning} onChange={upd("about.positioning")} textarea rows={4} />
            <Field label="Lien BuildingLab" value={s.about?.buildinglab_url} onChange={upd("about.buildinglab_url")} />
          </TabsContent>

          <TabsContent value="contact" className="space-y-4">
            <Field label="E-mail" value={s.contact?.email} onChange={upd("contact.email")} />
            <Field label="Adresse" value={s.contact?.address} onChange={upd("contact.address")} />
            <Field label="LinkedIn" value={s.contact?.linkedin} onChange={upd("contact.linkedin")} />
            <Field label="Facebook" value={s.contact?.facebook} onChange={upd("contact.facebook")} />
          </TabsContent>

          <TabsContent value="home" className="space-y-4">
            <p className="text-sm text-f2b-gray bg-f2b-light p-3 border border-f2b-filet">Le mode de la Home est désormais <strong>déduit automatiquement</strong> du <strong>statut de l'édition mise en avant</strong> (dans Éditions) : statut <em>« Passée »</em> → Home en <strong>rétrospective</strong> ; statut <em>« À venir »</em> ou <em>« Active »</em> → Home de <strong>préparation</strong> (S'inscrire / Être informé). Pour changer l'année affichée, cochez simplement « Édition active du site » sur la bonne fiche Édition — plus besoin de ce réglage.</p>
            <div>
              <label className="block text-sm font-medium text-f2b-text mb-1.5">Mode de la Home <span className="text-f2b-gray font-normal">(information technique — non utilisé par le site)</span></label>
              <Select value={s.home_mode || "before"} onValueChange={upd("home_mode")}>
                <SelectTrigger className="w-72" data-testid="home-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="before">Avant l'événement</SelectItem>
                  <SelectItem value="after">Après l'événement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          <TabsContent value="themes" className="space-y-4">
            <p className="text-sm text-f2b-gray">Liste contrôlée des thématiques d'articles. Elle alimente le sélecteur dans « Articles & Expertises » et les filtres du site public.</p>
            {(s.article_themes || []).map((t, i) => (
              <div key={i} className="flex gap-3 items-center">
                <Input value={t} data-testid={`theme-input-${i}`}
                  onChange={(e) => setS((p) => { const a = [...(p.article_themes || [])]; a[i] = e.target.value; return { ...p, article_themes: a }; })} />
                <button onClick={() => setS((p) => ({ ...p, article_themes: p.article_themes.filter((_, idx) => idx !== i) }))}
                  className="text-f2b-gray hover:text-red-500" data-testid={`theme-remove-${i}`}><Trash2 size={18} /></button>
              </div>
            ))}
            <Button variant="outline" data-testid="theme-add"
              onClick={() => setS((p) => ({ ...p, article_themes: [...(p.article_themes || []), ""] }))}>
              <Plus size={16} className="mr-1" /> Ajouter une thématique
            </Button>
          </TabsContent>

          <TabsContent value="seo" className="space-y-4">
            <Field label="Title par défaut" value={s.seo?.default_title} onChange={upd("seo.default_title")} />
            <Field label="Meta description par défaut" value={s.seo?.default_description} onChange={upd("seo.default_description")} textarea />
            <Field label="URL du site" value={s.seo?.site_url} onChange={upd("seo.site_url")} />
            <div>
              <label className="block text-sm font-medium text-f2b-text mb-1.5">Image Open Graph</label>
              <MediaField value={s.seo?.og_image} onChange={upd("seo.og_image")} />
            </div>
          </TabsContent>

          <TabsContent value="legal" className="space-y-4">
            <Field label="Titre" value={s.legal?.title} onChange={upd("legal.title")} />
            <Field label="Contenu" value={s.legal?.content} onChange={upd("legal.content")} textarea rows={8} />
          </TabsContent>
        </Tabs>
      </AdminBody>
    </>
  );
};

export default SettingsAdmin;
